import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useFocusTrap } from "@/shared/lib/useFocusTrap";
import { AppSidebar } from "../AppSidebar/AppSidebar";
import type { NavMap } from "../navigation/navigation";
import type { ShellUser } from "../AppShell/useShellUser";

export interface NavDrawerProps {
  id: string;
  open: boolean;
  onClose: () => void;
  nav: NavMap;
  user: ShellUser | null;
}

/**
 * La barra lateral como cajón, en móvil.
 *
 * ── Monta la MISMA barra ─────────────────────────────────────────────
 * No es una versión reducida: es `AppSidebar` con `onClose`. Una segunda
 * navegación para móvil es la que se queda sin el enlace que alguien
 * añadió solo en la de escritorio.
 *
 * ── No se monta cuando está cerrado ──────────────────────────────────
 * Así sus enlaces no participan en el orden de tabulación de la pantalla
 * que hay debajo. Un cajón oculto con `display:none` no molesta, pero uno
 * escondido con transformaciones sí, y es un fallo que solo se nota
 * tabulando.
 *
 * ── El foco queda atrapado ───────────────────────────────────────────
 * `aria-modal="true"` le promete al lector de pantalla que lo de fuera
 * está inerte. Sin la trampa de foco esa promesa es mentira: el tabulador
 * se escapa a la página de debajo mientras el lector sigue diciendo que
 * no existe. `useFocusTrap` también devuelve el foco al botón que abrió
 * el cajón cuando se cierra.
 *
 * ── El fondo es un `<button>` ────────────────────────────────────────
 * No un `<div onClick>`: así tiene rol, nombre y es alcanzable. Aunque
 * con el foco atrapado no se llegue con el tabulador, Escape cierra — y
 * quien usa un puntero o toca la pantalla sí lo pulsa.
 */
export function NavDrawer({ id, open, onClose, nav, user }: NavDrawerProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  useFocusTrap(panelRef, open, { onEscape: onClose });

  /*
   * Con el cajón abierto, la página de debajo no se desplaza. Sin esto,
   * arrastrar sobre el velo mueve el contenido de atrás y al cerrar la
   * pantalla ha cambiado de sitio sola.
   */
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    /*
     * `lg:hidden` de vuelta: el botón "Menú" del panel volvió a existir
     * solo bajo `lg` (Anahí, 27-ago: en escritorio la barra lateral YA
     * es la navegación), así que el cajón vuelve a ser cosa de móvil.
     */
    <div className="fixed inset-0 z-[80] lg:hidden">
      <button
        type="button"
        onClick={onClose}
        aria-label={t("Cerrar navegación")}
        className="absolute inset-0 h-full w-full cursor-default bg-ds-ink/35 backdrop-blur-sm"
      />

      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label={t("Navegación principal")}
        className="absolute inset-y-0 left-0 w-[19rem] max-w-[86vw] p-3"
      >
        {/*
          El cajón lleva el vidrio MÁS OPACO que la barra de escritorio, y
          no por gusto. En escritorio el panel flota sobre el fondo crema
          con sus halos, que es una superficie calmada; aquí flota sobre la
          pantalla real, y con `bg-white/35` la tarjeta violeta del
          contenido se transparentaba justo detrás de los enlaces. Se veía
          bonito y se leía mal.
        */}
        <AppSidebar
          nav={nav}
          user={user}
          onClose={onClose}
          onNavigate={onClose}
          className="h-full bg-white/90"
        />
      </div>
    </div>
  );
}
